import React, { useEffect, useState } from 'react';
import PublicPageWrapper from '../../components/PublicPageWrapper';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Calendar, Newspaper, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function EventCalendar({ events }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const getEventsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  return (
    <div data-testid="event-calendar">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={prevMonth} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
        <h3 className="font-bold text-[#1B2A4A] text-sm">{MONTHS[month]} {year}</h3>
        <Button variant="ghost" size="sm" onClick={nextMonth} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {WEEKDAYS.map(d => (
          <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-1">{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-px">
        {days.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} className="h-9" />;
          const dayEvents = getEventsForDate(day);
          const isToday = new Date().getFullYear() === year && new Date().getMonth() === month && new Date().getDate() === day;
          const isSelected = selectedDate === day;
          return (
            <button
              key={day}
              onClick={() => setSelectedDate(isSelected ? null : day)}
              className={`h-9 rounded-lg text-xs font-medium relative transition-colors ${
                isSelected ? 'bg-[#1B2A4A] text-white' :
                isToday ? 'bg-amber-100 text-[#1B2A4A] font-bold' :
                dayEvents.length > 0 ? 'bg-slate-50 text-[#1B2A4A] hover:bg-slate-100' :
                'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {day}
              {dayEvents.length > 0 && (
                <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-[#E8A830]'}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected date events */}
      {selectedDate && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-400 mb-2">{selectedDate} {MONTHS[month]}</p>
          {selectedEvents.length === 0 ? (
            <p className="text-xs text-slate-400">No events on this date</p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map(e => (
                <div key={e.id} className="bg-slate-50 rounded-lg p-3">
                  <Badge className="bg-[#1B2A4A] text-white text-[9px] mb-1">{e.event_type}</Badge>
                  <p className="font-bold text-xs text-[#1B2A4A]">{e.title}</p>
                  {e.time && <p className="text-[10px] text-[#E8A830] font-semibold">{e.time}</p>}
                  {e.description && <p className="text-[10px] text-slate-500 mt-1">{e.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All upcoming events list below calendar */}
      <div className="mt-5 pt-4 border-t border-slate-100">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">All Events</p>
        {events.length === 0 ? (
          <p className="text-xs text-slate-400">No upcoming events.</p>
        ) : (
          <div className="space-y-2">
            {events.map(e => (
              <div key={e.id} className="bg-white rounded-lg border border-slate-100 p-3 hover:shadow-sm transition-shadow">
                <Badge className="bg-[#1B2A4A] text-white text-[9px] mb-1">{e.event_type}</Badge>
                <p className="font-bold text-xs text-[#1B2A4A]">{e.title}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {e.date ? new Date(e.date).toLocaleDateString('en-ZW', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                </p>
                {e.time && <p className="text-[10px] text-[#E8A830] font-semibold">{e.time}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewsEventsPage() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/public/news`).then(r => setNews(r.data)).catch(() => {}),
      axios.get(`${API}/api/public/events`).then(r => setEvents(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <PublicPageWrapper title="News & Events" subtitle="Stay updated with the latest from Tadam Academy.">
      <div data-testid="news-events-page">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-lg font-bold text-[#1B2A4A] mb-5 flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-[#E8A830]" /> Latest News
              </h2>
              {news.length === 0 ? (
                <p className="text-slate-400 text-sm py-8 text-center">No news articles published yet.</p>
              ) : (
                <div className="space-y-4">
                  {news.map(n => (
                    <Card key={n.id} className="hover:shadow-md transition-shadow border-slate-100 overflow-hidden">
                      {n.image_url && (
                        <img
                          src={n.image_url.startsWith('http') ? n.image_url : `${API}/api/public/files/${n.image_url}`}
                          alt={n.title}
                          className="w-full max-h-80 object-contain bg-slate-50"
                          data-testid={`news-image-${n.id}`}
                        />
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs border-[#E8A830]/30 text-[#E8A830]">{n.category}</Badge>
                          <span className="text-xs text-slate-400">
                            {n.date ? new Date(n.date).toLocaleDateString('en-ZW', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                          </span>
                        </div>
                        <h3 className="font-bold text-[#1B2A4A] mb-1">{n.title}</h3>
                        <p className="text-sm text-slate-500 whitespace-pre-wrap">{n.content}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1B2A4A] mb-5 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#E8A830]" /> Term Calendar
              </h2>
              <div className="bg-white rounded-xl border border-slate-100 p-4">
                <EventCalendar events={events} />
              </div>
            </div>
          </div>
        )}
      </div>
    </PublicPageWrapper>
  );
}
